import { Modal } from "bootstrap";
import type { AlertField } from "./types";

interface ScaleBand {
  upTo: number; // upper bound of this band, in the field's unit
  color: string;
  label: string;
}

interface FieldHelp {
  icon: string;
  title: string;
  unit: string;
  blurb: string;
  scale?: { min: number; max: number; bands: ScaleBand[]; marker: number };
  source?: string;
}

// Reference bands are orientation guides distilled from IMGW warning criteria
// (opady/wiatr/śnieg) and the WHO UV Index scale — not exact legal thresholds,
// which vary by duration and region. Good enough to answer "is 20mm a lot?".
const FIELD_HELP: Partial<Record<AlertField, FieldHelp>> = {
  precipitation: {
    icon: "🌧️",
    title: "Opady (suma, mm)",
    unit: "mm",
    blurb:
      "1 mm to warstwa wody o grubości 1 mm na całej powierzchni. Do ok. 10 mm to zwykły deszcz, prawie niezauważalny w bilansie. IMGW zaczyna ostrzegać od ok. 20 mm/6h (ulewa, st. 1), a lokalne podtopienia zdarzają się już przy 40–60 mm.",
    scale: { min: 0, max: 60, marker: 20, bands: [
      { upTo: 10, color: "#4ade80", label: "lekki" },
      { upTo: 20, color: "#f2c744", label: "umiarkowany" },
      { upTo: 40, color: "#d9720e", label: "ulewa" },
      { upTo: 60, color: "#9e2b25", label: "nawałnica" },
    ] },
    source: "kryteria zbliżone do IMGW (ostrzeżenia I–III st.)",
  },
  snowfall: {
    icon: "❄️",
    title: "Opady śniegu (przyrost pokrywy)",
    unit: "cm",
    blurb:
      "Przyrost grubości pokrywy śnieżnej. Od ok. 5 cm/24h robi się to już uciążliwe dla ruchu drogowego, powyżej 15–20 cm IMGW ogłasza wyższe stopnie ostrzeżeń — to poziom, przy którym drogi bywają zamykane.",
    scale: { min: 0, max: 30, marker: 5, bands: [
      { upTo: 5, color: "#4ade80", label: "lekki" },
      { upTo: 10, color: "#f2c744", label: "umiarkowany" },
      { upTo: 20, color: "#d9720e", label: "silny" },
      { upTo: 30, color: "#9e2b25", label: "śnieżyca" },
    ] },
    source: "kryteria zbliżone do IMGW",
  },
  precipitation_probability: {
    icon: "🎯",
    title: "Prawdopodobieństwo opadów",
    unit: "%",
    blurb:
      "Szansa, że w danej godzinie/dniu w ogóle spadnie deszcz lub śnieg — nie mówi nic o ilości. Poniżej 30% zwykle zostaniesz suchy, powyżej 70% warto już zakładać, że będzie padać i brać parasol na pewniaka.",
    scale: { min: 0, max: 100, marker: 70, bands: [
      { upTo: 30, color: "#4ade80", label: "małe" },
      { upTo: 60, color: "#f2c744", label: "umiarkowane" },
      { upTo: 80, color: "#d9720e", label: "duże" },
      { upTo: 100, color: "#9e2b25", label: "bardzo duże" },
    ] },
  },
  wind_speed: {
    icon: "💨",
    title: "Wiatr (średnia prędkość)",
    unit: "m/s",
    blurb:
      "Średnia prędkość, nie porywy. Ok. 8 m/s (5°B skali Beauforta) to już wyraźnie odczuwalny, „świeży” wiatr — gałęzie w ciągłym ruchu. Od ok. 11–14 m/s (6–7°B) robi się to, co potocznie nazywamy silnym wiatrem.",
    scale: { min: 0, max: 25, marker: 12, bands: [
      { upTo: 5, color: "#4ade80", label: "słaby" },
      { upTo: 11, color: "#f2c744", label: "umiarkowany" },
      { upTo: 17, color: "#d9720e", label: "silny" },
      { upTo: 25, color: "#9e2b25", label: "bardzo silny" },
    ] },
    source: "skala Beauforta",
  },
  wind_gust: {
    icon: "🌬️",
    title: "Porywy wiatru",
    unit: "m/s",
    blurb:
      "Krótkie, mocniejsze uderzenia wiatru — to one łamią gałęzie i przewracają rzeczy, nie średnia prędkość. IMGW zaczyna ostrzegać zwykle od porywów ok. 65–70 km/h, czyli ok. 18–19 m/s.",
    scale: { min: 0, max: 30, marker: 20, bands: [
      { upTo: 10, color: "#4ade80", label: "słabe" },
      { upTo: 18, color: "#f2c744", label: "zauważalne" },
      { upTo: 25, color: "#d9720e", label: "silne" },
      { upTo: 30, color: "#9e2b25", label: "bardzo silne" },
    ] },
    source: "kryteria zbliżone do IMGW",
  },
  uv_index: {
    icon: "☀️",
    title: "Indeks UV",
    unit: "",
    blurb:
      "Skala WHO opisująca natężenie promieniowania UV. Od poziomu 3 zaleca się już ochronę (krem, okulary, cień w południe), a 8+ to poziom, przy którym niezabezpieczona skóra może się poparzyć w kilkanaście minut.",
    scale: { min: 0, max: 12, marker: 8, bands: [
      { upTo: 2, color: "#4ade80", label: "niski" },
      { upTo: 5, color: "#f2c744", label: "umiarkowany" },
      { upTo: 7, color: "#d9720e", label: "wysoki" },
      { upTo: 12, color: "#9e2b25", label: "bardzo wysoki / ekstremalny" },
    ] },
    source: "skala UV Index, WHO",
  },
  temperature_max: {
    icon: "🌡️",
    title: "Temperatura",
    unit: "°C",
    blurb:
      "Zwykła temperatura powietrza w cieniu, sprawdzana godzina po godzinie (nie dobowe maksimum/minimum) — dostaniesz zakres godzin, w których próg jest przekroczony. Powyżej 30°C IMGW ogłasza ostrzeżenia przed upałem, poniżej 0°C to już mróz, a poniżej -10°C — silny mróz, przy którym wychłodzenie organizmu robi się realnym ryzykiem. Wybierz „powyżej” dla upału albo „poniżej” dla mrozu — pole „Temp. maks. / min.” w dropdownie to tylko dwie nazwy tej samej godzinowej temperatury, liczy się kierunek porównania, nie która etykieta.",
    scale: { min: -25, max: 40, marker: 30, bands: [
      { upTo: -10, color: "#3498db", label: "silny mróz" },
      { upTo: 0, color: "#4ade80", label: "mróz / chłodno" },
      { upTo: 25, color: "#f2c744", label: "neutralnie" },
      { upTo: 30, color: "#d9720e", label: "ciepło" },
      { upTo: 40, color: "#9e2b25", label: "upał" },
    ] },
  },
  temperature_min: {
    icon: "🌡️",
    title: "Temperatura",
    unit: "°C",
    blurb:
      "Dokładnie ten sam godzinowy odczyt co „Temp. maks.” w dropdownie — nie dobowe minimum. Ustaw „poniżej” dla mrozu, „powyżej” dla upału; sama nazwa pola nic tu nie zmienia.",
    scale: { min: -25, max: 40, marker: 0, bands: [
      { upTo: -10, color: "#3498db", label: "silny mróz" },
      { upTo: 0, color: "#4ade80", label: "mróz / chłodno" },
      { upTo: 25, color: "#f2c744", label: "neutralnie" },
      { upTo: 30, color: "#d9720e", label: "ciepło" },
      { upTo: 40, color: "#9e2b25", label: "upał" },
    ] },
  },
  apparent_temperature_max: {
    icon: "🥵",
    title: "Temperatura odczuwalna",
    unit: "°C",
    blurb:
      "To, jak temperatura jest odbierana przez ciało — uwzględnia wiatr i wilgotność, więc bywa mocno inna niż termometr. Sprawdzana godzina po godzinie, nie jako dobowe maksimum. Odczuwalne 32°C+ to już realne obciążenie dla organizmu, poniżej -10°C skóra może odmrozić się w kilkanaście minut.",
    scale: { min: -25, max: 40, marker: 32, bands: [
      { upTo: -10, color: "#3498db", label: "przenikliwe zimno" },
      { upTo: 0, color: "#4ade80", label: "chłodno" },
      { upTo: 27, color: "#f2c744", label: "neutralnie" },
      { upTo: 32, color: "#d9720e", label: "gorąco" },
      { upTo: 40, color: "#9e2b25", label: "upał (odczuwalny)" },
    ] },
  },
  apparent_temperature_min: {
    icon: "🥶",
    title: "Temperatura odczuwalna",
    unit: "°C",
    blurb:
      "Dokładnie ten sam godzinowy odczyt co „Temp. odczuwalna maks.” w dropdownie, nie dobowe minimum. Ustaw „poniżej” dla przenikliwego zimna, „powyżej” dla upału odczuwalnego.",
    scale: { min: -25, max: 40, marker: -10, bands: [
      { upTo: -10, color: "#3498db", label: "przenikliwe zimno" },
      { upTo: 0, color: "#4ade80", label: "chłodno" },
      { upTo: 27, color: "#f2c744", label: "neutralnie" },
      { upTo: 32, color: "#d9720e", label: "gorąco" },
      { upTo: 40, color: "#9e2b25", label: "upał (odczuwalny)" },
    ] },
  },
  fog: {
    icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="3" y1="7" x2="21" y2="7"></line><line x1="5" y1="12" x2="19" y2="12"></line><line x1="3" y1="17" x2="21" y2="17"></line></svg>',
    title: "Mgła",
    unit: "",
    blurb:
      "Pole włącz/wyłącz, bez progu liczbowego — dostaniesz powiadomienie, gdy prognoza na daną godzinę przewiduje mgłę ograniczającą widoczność. Przydatne głównie pod kątem porannych dojazdów.",
  },
  thunderstorm: {
    icon: "⛈️",
    title: "Burza z piorunami",
    unit: "",
    blurb:
      "Pole włącz/wyłącz — alarm, gdy prognoza wskazuje na burzę w danej godzinie, niezależnie od jej siły. Dla samej siły opadów towarzyszących burzy lepiej dodać osobną regułę na „Opady”.",
  },
  freezing_rain: {
    icon: "🧊",
    title: "Marznący deszcz / gołoledź",
    unit: "",
    blurb:
      "Pole włącz/wyłącz — opady przy temperaturze blisko 0°C, które zamarzają momentalnie po kontakcie z chłodną nawierzchnią. Jedno z bardziej niebezpiecznych zjawisk drogowych, mimo pozornie niewielkiej ilości wody.",
  },
};

const FIELD_ORDER: AlertField[] = [
  "precipitation",
  "precipitation_probability",
  "snowfall",
  "wind_speed",
  "wind_gust",
  "temperature_max",
  "apparent_temperature_max",
  "uv_index",
  "fog",
  "thunderstorm",
  "freezing_rain",
];

function scaleHtml(help: FieldHelp): string {
  if (!help.scale) return "";
  const { min, max, bands, marker } = help.scale;
  const span = max - min;
  let prevUpTo = min;
  const segments = bands
    .map((band) => {
      const width = ((band.upTo - prevUpTo) / span) * 100;
      const seg = `<div class="help-scale-seg" style="width:${width}%;background:${band.color}" title="${band.label}"></div>`;
      prevUpTo = band.upTo;
      return seg;
    })
    .join("");
  const markerPct = Math.min(100, Math.max(0, ((marker - min) / span) * 100));
  return `
    <div class="help-scale">
      <div class="help-scale-track" data-min="${min}" data-max="${max}" data-unit="${help.unit}">
        ${segments}
        <div class="help-scale-marker" style="left:${markerPct}%" data-value="${marker}" tabindex="0" role="slider"
             aria-valuemin="${min}" aria-valuemax="${max}" aria-valuenow="${marker}"
             aria-label="Przeciągnij, aby zobaczyć próg na tle skali">
          <span class="help-scale-marker-value">${marker}${help.unit}</span>
        </div>
      </div>
      <div class="help-scale-ends">
        <span>${min}${help.unit}</span>
        <span>${max}${help.unit}</span>
      </div>
      <div class="help-scale-legend">
        ${bands.map((b) => `<span class="help-scale-legend-item"><i style="background:${b.color}"></i>${b.label}</span>`).join("")}
      </div>
    </div>
  `;
}

// Purely exploratory: dragging moves the marker and updates its value/position on
// this reference scale so you can see e.g. "20mm lands in the orange band" — it has
// no connection to any actual subscription rule or form field.
function initScaleDragging(root: HTMLElement): void {
  for (const track of root.querySelectorAll<HTMLElement>(".help-scale-track")) {
    const marker = track.querySelector<HTMLElement>(".help-scale-marker");
    const valueEl = marker?.querySelector<HTMLElement>(".help-scale-marker-value");
    if (!marker || !valueEl) continue;

    const min = Number(track.dataset.min);
    const max = Number(track.dataset.max);
    const unit = track.dataset.unit ?? "";

    const setValue = (value: number) => {
      const clamped = Math.min(max, Math.max(min, Math.round(value)));
      marker.style.left = `${((clamped - min) / (max - min)) * 100}%`;
      marker.dataset.value = String(clamped);
      marker.setAttribute("aria-valuenow", String(clamped));
      valueEl.textContent = `${clamped}${unit}`;
    };

    const valueFromClientX = (clientX: number): number => {
      const rect = track.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      return min + ratio * (max - min);
    };

    let dragging = false;

    track.addEventListener("pointerdown", (event) => {
      dragging = true;
      track.setPointerCapture?.(event.pointerId);
      setValue(valueFromClientX(event.clientX));
      event.preventDefault();
    });
    track.addEventListener("pointermove", (event) => {
      if (dragging) setValue(valueFromClientX(event.clientX));
    });
    track.addEventListener("pointerup", () => {
      dragging = false;
    });
    track.addEventListener("pointercancel", () => {
      dragging = false;
    });

    marker.addEventListener("keydown", (event) => {
      const current = Number(marker.dataset.value);
      if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
        setValue(current - 1);
        event.preventDefault();
      } else if (event.key === "ArrowRight" || event.key === "ArrowUp") {
        setValue(current + 1);
        event.preventDefault();
      }
    });
  }
}

function fieldCardHtml(field: AlertField): string {
  const help = FIELD_HELP[field];
  if (!help) return "";
  return `
    <div class="help-field">
      <div class="help-field-head">
        <span class="help-field-icon">${help.icon}</span>
        <span class="help-field-title">${help.title}</span>
      </div>
      <p class="help-field-blurb">${help.blurb}</p>
      ${scaleHtml(help)}
      ${help.source ? `<div class="help-field-source">${help.source}</div>` : ""}
    </div>
  `;
}

function buildHelpHtml(): string {
  const seen = new Set<string>();
  const cards = FIELD_ORDER
    .filter((field) => {
      const title = FIELD_HELP[field]?.title;
      if (!title || seen.has(title)) return false;
      seen.add(title);
      return true;
    })
    .map(fieldCardHtml)
    .join("");
  return `
    <p class="help-intro">
      Marker na pasku pokazuje domyślny próg alertu na tle orientacyjnej skali — tak wiesz od razu,
      czy np. 20&nbsp;mm deszczu to już „dużo", czy jeszcze mało.
    </p>
    ${cards}
  `;
}

export function initThresholdHelp(ids: { openButtons: string[]; modal: string; closeButton: string; body: string }): void {
  const openButtons = ids.openButtons
    .map((id) => document.getElementById(id))
    .filter((el): el is HTMLButtonElement => el !== null);
  const modalEl = document.getElementById(ids.modal) as HTMLElement;
  const closeButton = document.getElementById(ids.closeButton) as HTMLButtonElement;
  const body = document.getElementById(ids.body) as HTMLElement;

  let rendered = false;
  // Bootstrap's Modal owns show/hide, focus-trapping, Escape-to-close, and
  // click-outside-to-close — this only fills the body once on first open.
  const modal = new Modal(modalEl);

  modalEl.addEventListener("show.bs.modal", () => {
    if (rendered) return;
    body.innerHTML = buildHelpHtml();
    initScaleDragging(body);
    rendered = true;
  });

  for (const openButton of openButtons) {
    openButton.addEventListener("click", (event) => {
      event.preventDefault();
      modal.show();
    });
  }
  closeButton.addEventListener("click", () => modal.hide());
}
